import type { Language } from '../types';
import type { ShareLaunchPayload } from '../utils/sharePayload';
import { extractShareContextText } from '../utils/sharePayload';

const SHARE_IMPORT_API = '/api/mealpai-ai/share-import-recipe';

export interface ServerShareImportRequest {
    readonly url: string;
    readonly userId: string;
    readonly householdId: string;
    readonly targetLanguage: Language;
    readonly userEncryptedKey?: string;
    readonly payload: ShareLaunchPayload;
}

export type ServerShareImportResult =
    | { readonly status: 'saved'; readonly recipeId: string; readonly recipeName: string }
    | { readonly status: 'failed'; readonly code: string; readonly messageKey: string; readonly errorMessage?: string }
    | { readonly status: 'network_error'; readonly errorMessage: string };

export async function requestServerShareImport(
    input: ServerShareImportRequest,
): Promise<ServerShareImportResult> {
    const shareContextText = extractShareContextText(input.payload);

    try {
        const response = await fetch(SHARE_IMPORT_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            body: JSON.stringify({
                url: input.url,
                userId: input.userId,
                householdId: input.householdId,
                targetLanguage: input.targetLanguage,
                userEncryptedKey: input.userEncryptedKey,
                text: shareContextText || undefined,
                rawPayload: input.payload,
            }),
        });

        const raw = await response.text().catch(() => '');
        let parsed: Record<string, unknown> = {};
        if (raw.trim()) {
            try {
                parsed = JSON.parse(raw) as Record<string, unknown>;
            } catch {
                parsed = { error: raw };
            }
        }

        if (response.ok && parsed.status === 'saved') {
            return {
                status: 'saved',
                recipeId: String(parsed.recipeId ?? ''),
                recipeName: String(parsed.recipeName ?? ''),
            };
        }

        if (response.status === 422 || parsed.status === 'failed') {
            return {
                status: 'failed',
                code: String(parsed.code ?? 'unknown'),
                messageKey: String(parsed.messageKey ?? 'shareImportFailedTitle'),
                errorMessage: typeof parsed.errorMessage === 'string' ? parsed.errorMessage : undefined,
            };
        }

        return {
            status: 'network_error',
            errorMessage: typeof parsed.error === 'string'
                ? parsed.error
                : `Share import request failed with status ${response.status}.`,
        };
    } catch (error) {
        return {
            status: 'network_error',
            errorMessage: error instanceof Error ? error.message : String(error),
        };
    }
}
